import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  PendingActions as PendingIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAppSelector } from '../../store';
import { PurchaseRequest, RequestStatus } from '../../types/request.types';
import { API_CONFIG } from '../../config/api';

interface EmployeeStats {
  totalRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
  pendingRequests: number;
  draftRequests: number;
  totalAmountRequested: number;
  approvedAmount: number;
  averageRequestAmount: number;
}

interface CategoryStats {
  category: string;
  count: number;
  totalAmount: number;
  averageAmount: number;
}

interface MonthlyStats {
  month: string;
  requestCount: number;
  totalAmount: number;
}

const EmployeeReports: React.FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector(state => state.auth.user);
  const [requests, setRequests] = useState<PurchaseRequest[]>([]);
  const [stats, setStats] = useState<EmployeeStats | null>(null);
  const [categoryStats, setCategoryStats] = useState<CategoryStats[]>([]);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchEmployeeData();
  }, []);

  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_CONFIG.BASE_URL}/requests?myRequestsOnly=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch your requests');
      }

      const myRequests: PurchaseRequest[] = await response.json();
      setRequests(myRequests);

      // Calculate personal statistics
      calculatePersonalStats(myRequests);
      calculateCategoryStats(myRequests);
      calculateMonthlyStats(myRequests);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load your request data');
    } finally {
      setLoading(false);
    }
  };

  const calculatePersonalStats = (myRequests: PurchaseRequest[]) => {
    const approved = myRequests.filter(r => 
      r.status === RequestStatus.APPROVED || 
      r.status === RequestStatus.PAID || 
      r.status === RequestStatus.CLOSED
    );
    const rejected = myRequests.filter(r => r.status === RequestStatus.REJECTED);
    const pending = myRequests.filter(r => 
      r.status === RequestStatus.PENDING_MANAGER || 
      r.status === RequestStatus.PENDING_GENERAL_MANAGER ||
      r.status === RequestStatus.PENDING_SENIOR_MANAGER ||
      r.status === RequestStatus.PENDING_FINANCE ||
      r.status === RequestStatus.PENDING_CFO ||
      r.status === RequestStatus.MORE_INFO_NEEDED
    );
    const drafts = myRequests.filter(r => r.status === RequestStatus.DRAFT);

    const totalAmountRequested = myRequests.reduce((sum, r) => sum + r.amount, 0);
    const approvedAmount = approved.reduce((sum, r) => sum + r.amount, 0);

    setStats({
      totalRequests: myRequests.length,
      approvedRequests: approved.length,
      rejectedRequests: rejected.length,
      pendingRequests: pending.length,
      draftRequests: drafts.length,
      totalAmountRequested,
      approvedAmount,
      averageRequestAmount: myRequests.length > 0 ? totalAmountRequested / myRequests.length : 0,
    });
  };

  const calculateCategoryStats = (myRequests: PurchaseRequest[]) => {
    const categoryGroups = myRequests.reduce((groups: { [key: string]: PurchaseRequest[] }, request) => {
      const category = request.category || 'OTHER';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(request);
      return groups;
    }, {});

    const categoryStatsArray = Object.entries(categoryGroups).map(([category, categoryRequests]) => {
      const totalAmount = categoryRequests.reduce((sum, r) => sum + r.amount, 0);
      return {
        category,
        count: categoryRequests.length,
        totalAmount,
        averageAmount: totalAmount / categoryRequests.length,
      };
    });

    // Sort by total amount descending
    categoryStatsArray.sort((a, b) => b.totalAmount - a.totalAmount);
    setCategoryStats(categoryStatsArray);
  };

  const calculateMonthlyStats = (myRequests: PurchaseRequest[]) => {
    const monthlyGroups = myRequests.reduce((groups: { [key: string]: PurchaseRequest[] }, request) => {
      const month = format(new Date(request.createdAt), 'yyyy-MM');
      if (!groups[month]) {
        groups[month] = [];
      }
      groups[month].push(request);
      return groups;
    }, {});

    const monthlyStatsArray = Object.entries(monthlyGroups).map(([month, monthRequests]) => {
      const totalAmount = monthRequests.reduce((sum, r) => sum + r.amount, 0);
      return {
        month: format(new Date(month + '-01'), 'MMM yyyy'),
        requestCount: monthRequests.length,
        totalAmount,
      };
    });

    // Sort by month ascending (most recent first)
    monthlyStatsArray.sort((a, b) => new Date(b.month).getTime() - new Date(a.month).getTime());
    setMonthlyStats(monthlyStatsArray.slice(0, 6)); // Show last 6 months
  };

  const getStatusColor = (status: RequestStatus) => {
    switch (status) {
      case RequestStatus.APPROVED:
      case RequestStatus.PAID:
      case RequestStatus.CLOSED:
        return 'success';
      case RequestStatus.REJECTED:
        return 'error';
      case RequestStatus.PENDING_MANAGER:
      case RequestStatus.PENDING_GENERAL_MANAGER:
      case RequestStatus.PENDING_SENIOR_MANAGER:
      case RequestStatus.PENDING_FINANCE:
      case RequestStatus.PENDING_CFO:
        return 'warning';
      case RequestStatus.MORE_INFO_NEEDED:
        return 'info';
      case RequestStatus.DRAFT:
        return 'default';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getApprovalRate = () => {
    if (!stats || stats.totalRequests === 0) return 0;
    const processedRequests = stats.totalRequests - stats.draftRequests;
    if (processedRequests === 0) return 0;
    return ((stats.approvedRequests / processedRequests) * 100);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <IconButton 
          onClick={() => navigate('/dashboard')}
          sx={{ 
            backgroundColor: 'primary.main', 
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          My Purchase Request Reports
        </Typography>
        <PersonIcon sx={{ fontSize: 40, color: 'primary.main' }} />
      </Box>

      {/* Personal Statistics */}
      {stats && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="primary" />
            Personal Request Overview
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', backgroundColor: 'primary.light', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <TrendingUpIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" component="div">
                    {stats.totalRequests}
                  </Typography>
                  <Typography variant="body2">
                    Total Requests
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', backgroundColor: 'success.light', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ApprovedIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" component="div">
                    {stats.approvedRequests}
                  </Typography>
                  <Typography variant="body2">
                    Approved Requests
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', backgroundColor: 'warning.light', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <PendingIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" component="div">
                    {stats.pendingRequests}
                  </Typography>
                  <Typography variant="body2">
                    Pending Approval
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', backgroundColor: 'secondary.light', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <MoneyIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" component="div">
                    {formatCurrency(stats.totalAmountRequested)}
                  </Typography>
                  <Typography variant="body2">
                    Total Requested
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="success.main">
                  {formatCurrency(stats.approvedAmount)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Approved Amount
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary.main">
                  {formatCurrency(stats.averageRequestAmount)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average Request
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="info.main">
                  {getApprovalRate().toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Approval Rate
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="error.main">
                  {stats.rejectedRequests}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Rejected Requests
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Category Breakdown */}
      {categoryStats.length > 0 && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CategoryIcon color="primary" />
            Requests by Category
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell align="center"><strong>Requests</strong></TableCell>
                  <TableCell align="right"><strong>Total Amount</strong></TableCell>
                  <TableCell align="right"><strong>Average Amount</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categoryStats.map((category, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Chip 
                        label={category.category} 
                        variant="outlined" 
                        color="primary" 
                      />
                    </TableCell>
                    <TableCell align="center">{category.count}</TableCell>
                    <TableCell align="right">{formatCurrency(category.totalAmount)}</TableCell>
                    <TableCell align="right">{formatCurrency(category.averageAmount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Monthly Trends */}
      {monthlyStats.length > 0 && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon color="primary" />
            Monthly Request Trends (Last 6 Months)
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Month</strong></TableCell>
                  <TableCell align="center"><strong>Requests</strong></TableCell>
                  <TableCell align="right"><strong>Total Amount</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthlyStats.map((month, index) => (
                  <TableRow key={index}>
                    <TableCell>{month.month}</TableCell>
                    <TableCell align="center">{month.requestCount}</TableCell>
                    <TableCell align="right">{formatCurrency(month.totalAmount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Recent Requests */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon color="primary" />
          Recent Requests
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Title</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Amount</strong></TableCell>
                <TableCell align="center"><strong>Created</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.slice(0, 10).map((request) => (
                <TableRow key={request.id}>
                  <TableCell>{request.title}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={request.status} 
                      color={getStatusColor(request.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{formatCurrency(request.amount)}</TableCell>
                  <TableCell align="center">
                    {format(new Date(request.createdAt), 'MMM dd, yyyy')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {requests.length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No requests found. Start by creating your first purchase request!
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default EmployeeReports; 